import jsPDF from 'jspdf';

/**
 * jsPDF A4 default unit is **millimetres**. Sizes like 168×168 must be ~55-65mm for a chart,
 * not 168mm (that consumes the whole page and collides with the footer).
 */

const BRAND = { r: 89, g: 185, b: 193 };
const TEXT_DARK = { r: 55, g: 65, b: 81 };
const TEXT_MUTED = { r: 107, g: 114, b: 128 };
const BORDER_LIGHT = { r: 229, g: 231, b: 235 };
const CARD_BG = { r: 255, g: 255, b: 255 };
const PANEL_FILL = { r: 250, g: 251, b: 252 };
/** Strengths: light teal tint + teal border (brand) */
const STRENGTH_BOX_FILL = { r: 236, g: 252, b: 252 };
/** Opportunities: warm tint + brand yellow border (pairs with site accent) */
const BRAND_YELLOW = { r: 245, g: 184, b: 52 }; // #F5B834
const BRAND_YELLOW_TEXT = { r: 120, g: 83, b: 8 };
const OPPORTUNITY_BOX_FILL = { r: 255, g: 250, b: 235 };

function formatDimensionLabel(dim: string | string[]): string {
  return Array.isArray(dim) ? dim.join(' ') : dim;
}

function setBrandColor(doc: jsPDF) {
  doc.setTextColor(BRAND.r, BRAND.g, BRAND.b);
}

function setDarkText(doc: jsPDF) {
  doc.setTextColor(TEXT_DARK.r, TEXT_DARK.g, TEXT_DARK.b);
}

function setMutedText(doc: jsPDF) {
  doc.setTextColor(TEXT_MUTED.r, TEXT_MUTED.g, TEXT_MUTED.b);
}

export async function generatePDFReport(
  chartImageBase64: string,
  overallRating: number,
  strengths: Array<{ dimension: string | string[]; score: number; avg: number }>,
  improvements: Array<{ dimension: string | string[]; score: number; avg: number }>,
  returnBase64: boolean = false
) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const borderMargin = 12;
  const contentMargin = borderMargin + 10;
  const contentWidth = pageWidth - 2 * contentMargin;
  const borderRadius = 4;
  /** Space reserved at bottom for footer + breathing room above footnote */
  const footerReservedMm = 26;
  const footerTopY = pageHeight - footerReservedMm;
  const logoWidth = 20;

  let logoHeight = 0;
  let logoLoaded = false;

  try {
    const logoImg = new Image();
    logoImg.src = '/images/DJLogo2025_small.jpg';
    await new Promise<void>((resolve, reject) => {
      logoImg.onload = () => resolve();
      logoImg.onerror = () => reject(new Error('logo load failed'));
    });
    logoHeight = (logoImg.height * logoWidth) / logoImg.width;
    doc.addImage(logoImg, 'JPEG', pageWidth - logoWidth - borderMargin, borderMargin, logoWidth, logoHeight);
    logoLoaded = true;
  } catch {
    logoHeight = 0;
  }

  const borderTop = Math.max(logoLoaded ? logoHeight + borderMargin + 4 : 0, borderMargin);
  const panelBottomMargin = footerReservedMm + 2;

  const drawPanelBackground = (top: number, height: number) => {
    doc.setFillColor(PANEL_FILL.r, PANEL_FILL.g, PANEL_FILL.b);
    doc.roundedRect(
      borderMargin,
      top,
      pageWidth - 2 * borderMargin,
      height,
      borderRadius,
      borderRadius,
      'F'
    );
    doc.setDrawColor(BORDER_LIGHT.r, BORDER_LIGHT.g, BORDER_LIGHT.b);
    doc.setLineWidth(0.35);
    doc.roundedRect(
      borderMargin,
      top,
      pageWidth - 2 * borderMargin,
      height,
      borderRadius,
      borderRadius,
      'S'
    );
  };

  /** Stack footer from bottom up so copyright sits above a clear bottom margin */
  const drawFooter = (pageNum: number, totalPages: number) => {
    const bottomInsetMm = 8;
    let y = pageHeight - bottomInsetMm;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    setMutedText(doc);
    doc.text(`© ${new Date().getFullYear()} Data Jukebox. All rights reserved.`, pageWidth / 2, y, {
      align: 'center',
    });
    y -= 5;
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, y, { align: 'center' });
    y -= 5;
    doc.setFontSize(8);
    doc.text(`Assessment Date: ${new Date().toLocaleDateString()}`, pageWidth - contentMargin, y, {
      align: 'right',
    });
    doc.text('Contact: info@datajukebox.com', contentMargin, y);
    y -= 5;
    setBrandColor(doc);
    doc.text('www.datajukebox.com', contentMargin, y);
  };

  const drawAccentBar = (y: number, w: number) => {
    doc.setFillColor(BRAND.r, BRAND.g, BRAND.b);
    doc.rect(contentMargin, y, w, 0.6, 'F');
  };

  /** Chart side length in mm - larger to use page 1 whitespace (still leaves room for score + note) */
  const chartSizeMm = 88;
  const scoreRadiusMm = 7;

  // -- Page 1 --
  const page1PanelHeight = pageHeight - borderTop - panelBottomMargin;
  drawPanelBackground(borderTop, page1PanelHeight);

  let y = borderTop + 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  setBrandColor(doc);
  doc.text('HCP PERSONALIZATION BENCHMARK', contentMargin, y);
  y += 6;

  doc.setFontSize(11);
  setDarkText(doc);
  const titleLines = doc.splitTextToSize(
    'How well are you personalising your engagement with healthcare professionals?',
    contentWidth
  );
  doc.text(titleLines, contentMargin, y);
  y += titleLines.length * 4.8 + 4;

  drawAccentBar(y, contentWidth);
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  setBrandColor(doc);
  doc.text('Your benchmark results', pageWidth / 2, y, { align: 'center' });
  y += 8;

  if (chartImageBase64) {
    const cx = (pageWidth - chartSizeMm) / 2;
    doc.setFillColor(CARD_BG.r, CARD_BG.g, CARD_BG.b);
    doc.setDrawColor(BORDER_LIGHT.r, BORDER_LIGHT.g, BORDER_LIGHT.b);
    doc.setLineWidth(0.25);
    doc.roundedRect(cx - 2, y - 2, chartSizeMm + 4, chartSizeMm + 4, 2, 2, 'FD');
    doc.addImage(chartImageBase64, 'PNG', cx, y, chartSizeMm, chartSizeMm, undefined, 'SLOW');
    y += chartSizeMm + 8;
  } else {
    y += 4;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setMutedText(doc);
  doc.text('Seven dimensions vs industry average', pageWidth / 2, y, { align: 'center' });
  y += 9;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setDarkText(doc);
  doc.text('Your overall score', pageWidth / 2, y, { align: 'center' });
  y += 6;

  const scoreCenterX = pageWidth / 2;
  const scoreCenterY = y + scoreRadiusMm;
  doc.setDrawColor(BRAND.r, BRAND.g, BRAND.b);
  doc.setLineWidth(0.45);
  doc.setFillColor(245, 252, 251);
  doc.circle(scoreCenterX, scoreCenterY, scoreRadiusMm, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  setBrandColor(doc);
  doc.text(`${overallRating}%`, scoreCenterX, scoreCenterY + 2, { align: 'center' });

  y = scoreCenterY + scoreRadiusMm + 6;

  const aboveCount = strengths.length;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setDarkText(doc);
  doc.text(
    `You scored above the industry average in ${aboveCount} of 7 dimensions.`,
    pageWidth / 2,
    y,
    { align: 'center', maxWidth: contentWidth }
  );
  y += 8;

  doc.setFontSize(8);
  setMutedText(doc);
  const note =
    'This report compares your scores against the industry average. Leading pharmaceutical companies often aim towards best practice (100% in all dimensions) to maximise the benefits of HCP personalisation.';
  const noteLines = doc.splitTextToSize(note, contentWidth);
  let noteY = y;
  for (let i = 0; i < noteLines.length; i++) {
    if (noteY + 4 > footerTopY - 6) break;
    doc.text(noteLines[i], pageWidth / 2, noteY, { align: 'center', maxWidth: contentWidth });
    noteY += 3.8;
  }

  drawFooter(1, 2);

  // -- Page 2 --
  doc.addPage();
  const page2PanelHeight = pageHeight - borderTop - panelBottomMargin;
  drawPanelBackground(borderTop, page2PanelHeight);

  y = borderTop + 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  setBrandColor(doc);
  doc.text('Detailed breakdown', contentMargin, y);
  y += 6;
  drawAccentBar(y, contentWidth);
  y += 8;

  const rowLineHeight = 4.2;
  const sectionTitleSize = 10;
  const bodySize = 8.5;
  const innerPad = 3;

  const lineForItem = (label: string, score: number, avg: number) =>
    `• ${label}: You ${score}% | Industry avg ${avg}%`;

  const measureListBlock = (items: typeof strengths, emptyLine: string): number => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(bodySize);
    let h = 5 + 10 + 6;
    if (items.length === 0) {
      const w = doc.splitTextToSize(emptyLine, contentWidth - innerPad * 2);
      h += w.length * rowLineHeight + 3;
    } else {
      items.forEach((s) => {
        const label = formatDimensionLabel(s.dimension);
        const line = lineForItem(label, s.score, s.avg);
        const wrapped = doc.splitTextToSize(line, contentWidth - innerPad * 2);
        h += wrapped.length * rowLineHeight + 1.5;
      });
    }
    return h + 5;
  };

  const strengthsTitle = 'Strengths (above industry average)';
  const strengthsEmpty =
    'No dimensions above average on this run. Focus on the opportunities below.';
  const strengthsH = measureListBlock(strengths, strengthsEmpty);

  const strengthsBoxY = y;
  doc.setFillColor(STRENGTH_BOX_FILL.r, STRENGTH_BOX_FILL.g, STRENGTH_BOX_FILL.b);
  doc.setDrawColor(BRAND.r, BRAND.g, BRAND.b);
  doc.setLineWidth(0.4);
  doc.roundedRect(contentMargin, strengthsBoxY, contentWidth, strengthsH, 1.5, 1.5, 'FD');

  y = strengthsBoxY + 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(sectionTitleSize);
  setBrandColor(doc);
  doc.text(strengthsTitle, contentMargin + innerPad, y);
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(bodySize);
  setDarkText(doc);
  if (strengths.length === 0) {
    const w = doc.splitTextToSize(strengthsEmpty, contentWidth - innerPad * 2);
    doc.text(w, contentMargin + innerPad, y);
    y += w.length * rowLineHeight + 3;
  } else {
    strengths.forEach((s) => {
      const label = formatDimensionLabel(s.dimension);
      const line = lineForItem(label, s.score, s.avg);
      const wrapped = doc.splitTextToSize(line, contentWidth - innerPad * 2);
      doc.text(wrapped, contentMargin + innerPad, y);
      y += wrapped.length * rowLineHeight + 1.5;
    });
  }

  y = strengthsBoxY + strengthsH + 8;

  const improveTitle = 'Opportunities for growth';
  const improveEmpty = 'Strong performance: you are at or above average across all dimensions.';
  const improveH = measureListBlock(improvements, improveEmpty);

  const improveBoxY = y;
  doc.setFillColor(OPPORTUNITY_BOX_FILL.r, OPPORTUNITY_BOX_FILL.g, OPPORTUNITY_BOX_FILL.b);
  doc.setDrawColor(BRAND_YELLOW.r, BRAND_YELLOW.g, BRAND_YELLOW.b);
  doc.setLineWidth(0.4);
  doc.roundedRect(contentMargin, improveBoxY, contentWidth, improveH, 1.5, 1.5, 'FD');

  y = improveBoxY + 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(sectionTitleSize);
  doc.setTextColor(BRAND_YELLOW_TEXT.r, BRAND_YELLOW_TEXT.g, BRAND_YELLOW_TEXT.b);
  doc.text(improveTitle, contentMargin + innerPad, y);
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(bodySize);
  setDarkText(doc);
  if (improvements.length === 0) {
    const w = doc.splitTextToSize(improveEmpty, contentWidth - innerPad * 2);
    doc.text(w, contentMargin + innerPad, y);
    y += w.length * rowLineHeight + 3;
  } else {
    improvements.forEach((im) => {
      const label = formatDimensionLabel(im.dimension);
      const line = lineForItem(label, im.score, im.avg);
      const wrapped = doc.splitTextToSize(line, contentWidth - innerPad * 2);
      doc.text(wrapped, contentMargin + innerPad, y);
      y += wrapped.length * rowLineHeight + 1.5;
    });
  }

  y = improveBoxY + improveH + 8;

  const steps = [
    'Explore the 7 Dimensions framework and whitepaper at datajukebox.com.',
    'Book a tailored walkthrough to see doctor-level HCP insights in action.',
  ];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  let nextH = 6 + 6;
  steps.forEach((s) => {
    const w = doc.splitTextToSize(`• ${s}`, contentWidth - innerPad * 2);
    nextH += w.length * rowLineHeight + 1.5;
  });
  nextH += 5;

  doc.setFillColor(249, 250, 251);
  doc.setDrawColor(BORDER_LIGHT.r, BORDER_LIGHT.g, BORDER_LIGHT.b);
  doc.setLineWidth(0.25);
  doc.roundedRect(contentMargin, y, contentWidth, nextH, 1.5, 1.5, 'FD');
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setBrandColor(doc);
  doc.text('Next steps', contentMargin + innerPad, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setDarkText(doc);
  steps.forEach((s) => {
    const w = doc.splitTextToSize(`• ${s}`, contentWidth - innerPad * 2);
    doc.text(w, contentMargin + innerPad, y);
    y += w.length * rowLineHeight + 1.5;
  });

  drawFooter(2, 2);

  if (returnBase64) {
    return doc.output('datauristring').split(',')[1];
  }
  doc.save('benchmark-assessment-report.pdf');
}
